'use client';

/**
 * PageTile - Displays application page/navigation search results
 *
 * Shows page information including:
 * - Page name and route
 * - Section of the app
 * - Keywords
 * - Quick navigation indicator
 */

import {
  Layout,
  ExternalLink,
  Home,
  Settings,
  Users,
  FileText,
  BarChart3,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { createTile, type BaseTileRenderProps } from './BaseTile';
import type { PageResult } from '../types';

/**
 * Map icon names to components
 */
const iconMap: Record<string, LucideIcon> = {
  home: Home,
  settings: Settings,
  users: Users,
  documents: FileText,
  reports: BarChart3,
  help: HelpCircle,
  layout: Layout,
};

/**
 * Get icon for page
 */
function getPageIcon(iconName?: string, section?: string): LucideIcon {
  // Try icon name first
  if (iconName && iconMap[iconName.toLowerCase()]) {
    return iconMap[iconName.toLowerCase()];
  }

  // Try section name
  if (section && iconMap[section.toLowerCase()]) {
    return iconMap[section.toLowerCase()];
  }

  // Default
  return Layout;
}

/**
 * Get icon color based on section
 */
function getIconColor(section?: string): string {
  const sectionColors: Record<string, string> = {
    settings: 'text-slate-400',
    admin: 'text-red-400',
    reports: 'text-indigo-400',
    help: 'text-cyan-400',
    documents: 'text-purple-400',
    users: 'text-blue-400',
  };

  return sectionColors[section?.toLowerCase() ?? ''] ?? 'text-slate-400';
}

/**
 * Format route for display
 */
function formatRoute(route: string): string {
  // Remove leading slash and format nicely
  return route.replace(/^\//, '').replace(/\//g, ' → ') || 'Home';
}

/**
 * PageTile component
 */
export const PageTile = createTile<PageResult>(
  'PageTile',
  (result) => {
    const { metadata } = result;
    const Icon = getPageIcon(metadata.iconName, metadata.section);

    const renderProps: BaseTileRenderProps = {
      icon: Icon,
      iconColor: getIconColor(metadata.section),
      title: result.title,
      subtitle: formatRoute(metadata.route),
      preview: result.preview,
      metadata: [],
      tags: metadata.keywords?.slice(0, 3) || [],
      rightContent: (
        <div className="flex items-center gap-2">
          {metadata.section && (
            <Badge
              variant="outline"
              className="text-xs bg-slate-800/50 border-slate-700/50 text-slate-400"
            >
              {metadata.section}
            </Badge>
          )}
          <ExternalLink className="w-4 h-4 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      ),
    };

    // Add route as metadata
    renderProps.metadata!.push({
      label: 'Route',
      value: metadata.route,
    });

    return renderProps;
  }
);

export default PageTile;
